import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { GoogleLogo } from "@phosphor-icons/react";
import TotpChallenge from "@/pages/TotpChallenge";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { setUser, refresh } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // TOTP challenge state — set when /auth/login answers `requires_totp: true`
  const [totp, setTotp] = useState(null); // { email, hasTotp }

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.requires_totp) {
        setTotp({ email: data.email, hasTotp: !!data.has_totp });
        return;
      }
      setUser(data.user);
      toast.success("Bienvenue !");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connexion impossible");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // ---- TOTP step (superadmin only) ----
  if (totp) {
    return (
      <TotpChallenge
        email={totp.email}
        hasTotp={totp.hasTotp}
        onSuccess={async (user) => {
          setUser(user);
          await refresh(); // pick up is_superadmin + freshly-set cookie
          toast.success("Bienvenue !");
          nav(user?.is_superadmin ? "/superadmin" : "/dashboard");
        }}
        onCancel={() => setTotp(null)}
      />
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F4F7FB]">
      <div className="hidden lg:flex flex-col justify-between p-16 bg-[#1E3A5F] text-white relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2.5 relative z-10">
          <span className="w-8 h-8 rounded-[10px] bg-[#3A8FA5] flex items-center justify-center text-white font-bold text-base">Q</span>
          <span className="text-[18px] font-bold tracking-tight">Questiomed</span>
        </Link>
        <div className="relative z-10">
          <p className="font-serif text-4xl leading-tight italic">
            "Enfin un outil qui me fait vraiment gagner du temps"
          </p>
          <p className="mt-6 text-sm text-white/60 tracking-widest uppercase">— Dr D., médecin généraliste</p>
        </div>
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(ellipse at bottom right, #3A8FA5, transparent 60%)" }} />
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-8">
          <div>
            <Link to="/" className="flex items-center gap-2.5 mb-8 lg:hidden">
              <span className="w-8 h-8 rounded-[10px] bg-[#3A8FA5] flex items-center justify-center text-white font-bold text-base">Q</span>
              <span className="text-[18px] font-bold tracking-tight text-[#1E3A5F]">Questiomed</span>
            </Link>
            <h1 className="font-serif text-4xl mb-2">Bienvenue</h1>
            <p className="text-[#5F758F]">Connectez-vous à votre espace praticien.</p>
          </div>

          <Button
            type="button"
            onClick={googleLogin}
            variant="outline"
            className="w-full h-12 rounded-full border-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white flex items-center gap-3"
            data-testid="google-login-btn"
          >
            <GoogleLogo size={20} weight="bold" /> Continuer avec Google
          </Button>

          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-[#E1E9F0]" />
            <span className="text-xs tracking-widest uppercase text-[#5F758F]">ou</span>
            <div className="flex-1 h-px bg-[#E1E9F0]" />
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 bg-white border-[#E1E9F0] focus-visible:ring-[#3A8FA5]"
                data-testid="login-email" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase text-[#5F758F]">Mot de passe</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-12 bg-white border-[#E1E9F0] focus-visible:ring-[#3A8FA5]"
                data-testid="login-password" />
            </div>
          </div>

          <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#3A8FA5] hover:bg-[#164E5F] text-white" data-testid="login-submit">
            {busy ? "Connexion…" : "Se connecter"}
          </Button>

          <div className="flex items-center justify-between text-sm">
            <Link to="/forgot-password" className="text-[#5F758F] hover:text-[#1E3A5F] hover:underline underline-offset-4" data-testid="link-forgot">
              Mot de passe oublié ?
            </Link>
            <Link to="/signup" className="text-[#1E3A5F] underline underline-offset-4" data-testid="link-signup">
              Créer un compte
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
